import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './AuditTrail.css';

const MOCK_ENTRIES = [
  {
    id: '1',
    timestamp: '2025-02-23T14:32:00Z',
    submissionId: 'sub_abc123',
    actionType: 'ai_scoring',
    industryCode: '541512',
    industryName: 'Computer Systems Design',
    baseScore: 6,
    tagsApplied: [
      { label: 'MFA deployed', adjustment: -2 },
      { label: 'Encryption at rest', adjustment: -1 },
    ],
    finalScore: 3,
    confidence: 0.92,
    hazardTier: 'green',
    override: null,
  },
  {
    id: '2',
    timestamp: '2025-02-23T13:15:00Z',
    submissionId: 'sub_def456',
    actionType: 'manual_override',
    industryCode: '524126',
    industryName: 'Direct Property/Casualty Insurance',
    baseScore: 5,
    tagsApplied: [
      { label: 'No MFA', adjustment: 2 },
      { label: 'Ransomware history', adjustment: 3 },
    ],
    finalScore: 10,
    confidence: 0.78,
    hazardTier: 'red',
    override: { from: 10, to: 7, reason: 'UW reviewed: MFA implemented post-incident; verified via attestation.' },
  },
  {
    id: '3',
    timestamp: '2025-02-23T11:48:00Z',
    submissionId: 'sub_ghi789',
    actionType: 'decision',
    industryCode: '541611',
    industryName: 'Administrative Management Consulting',
    baseScore: 4,
    tagsApplied: [
      { label: 'MFA deployed', adjustment: -2 },
      { label: 'SOC 2 Type II', adjustment: -1 },
    ],
    finalScore: 1,
    confidence: 0.95,
    hazardTier: 'green',
    override: null,
  },
  {
    id: '4',
    timestamp: '2025-02-22T16:22:00Z',
    submissionId: 'sub_jkl012',
    actionType: 'ai_scoring',
    industryCode: '621511',
    industryName: 'Offices of Lawyers',
    baseScore: 7,
    tagsApplied: [
      { label: 'No encryption at rest', adjustment: 1 },
      { label: 'High-value data', adjustment: 2 },
    ],
    finalScore: 10,
    confidence: 0.68,
    hazardTier: 'red',
    override: null,
  },
  {
    id: '5',
    timestamp: '2025-02-22T10:05:00Z',
    submissionId: 'sub_mno345',
    actionType: 'manual_override',
    industryCode: '541511',
    industryName: 'Custom Computer Programming',
    baseScore: 8,
    tagsApplied: [
      { label: 'MFA deployed', adjustment: -2 },
      { label: 'EDR in place', adjustment: -1 },
    ],
    finalScore: 5,
    confidence: 0.85,
    hazardTier: 'orange',
    override: { from: 5, to: 4, reason: 'UW: Confirmed EDR coverage; tier adjusted to Moderate.' },
  },
  {
    id: '6',
    timestamp: '2025-02-21T09:30:00Z',
    submissionId: 'sub_pqr678',
    actionType: 'ai_scoring',
    industryCode: '541110',
    industryName: 'Offices of Lawyers',
    baseScore: 5,
    tagsApplied: [
      { label: 'MFA deployed', adjustment: -2 },
    ],
    finalScore: 3,
    confidence: 0.88,
    hazardTier: 'green',
    override: null,
  },
  {
    id: '7',
    timestamp: '2025-02-21T08:12:00Z',
    submissionId: 'sub_stu901',
    actionType: 'decision',
    industryCode: '524291',
    industryName: 'Claims Adjusting',
    baseScore: 6,
    tagsApplied: [
      { label: 'No MFA', adjustment: 2 },
      { label: 'Legacy systems', adjustment: 1 },
    ],
    finalScore: 9,
    confidence: 0.72,
    hazardTier: 'red',
    override: null,
  },
];

const HAZARD_LABELS = {
  green: 'Low',
  yellow: 'Moderate',
  orange: 'Moderate',
  red: 'High',
};

const ACTION_LABELS = {
  ai_scoring: 'AI Scoring',
  manual_override: 'Manual Override',
  decision: 'Decision',
};

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function AuditTrail() {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [product, setProduct] = useState('');
  const [decisionType, setDecisionType] = useState('');
  const [underwriter, setUnderwriter] = useState('');

  const filteredEntries = MOCK_ENTRIES.filter((entry) => {
    if (dateFrom && new Date(entry.timestamp) < new Date(dateFrom)) return false;
    if (dateTo && new Date(entry.timestamp) > new Date(dateTo + 'T23:59:59')) return false;
    if (decisionType && entry.actionType !== decisionType) return false;
    return true;
  });

  return (
    <div className="at">
      <h1 className="at__title">AI Decision Audit Trail</h1>

      <div className="at__filters card">
        <div className="at__filter-row">
          <div className="at__filter-group">
            <label className="at__filter-label">Date range</label>
            <div className="at__filter-row-inline">
              <input
                type="date"
                className="at__filter-input"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
              <span className="at__filter-sep">to</span>
              <input
                type="date"
                className="at__filter-input"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
          </div>
          <div className="at__filter-group">
            <label className="at__filter-label">Product</label>
            <select
              className="at__filter-select"
              value={product}
              onChange={(e) => setProduct(e.target.value)}
            >
              <option value="">All products</option>
              <option value="cyber">Cyber & Tech</option>
              <option value="e&o">E&O</option>
            </select>
          </div>
          <div className="at__filter-group">
            <label className="at__filter-label">Decision type</label>
            <select
              className="at__filter-select"
              value={decisionType}
              onChange={(e) => setDecisionType(e.target.value)}
            >
              <option value="">All types</option>
              <option value="ai_scoring">AI Scoring</option>
              <option value="manual_override">Manual Override</option>
              <option value="decision">Decision</option>
            </select>
          </div>
          <div className="at__filter-group">
            <label className="at__filter-label">Underwriter</label>
            <select
              className="at__filter-select"
              value={underwriter}
              onChange={(e) => setUnderwriter(e.target.value)}
            >
              <option value="">All</option>
              <option value="jane">Jane Smith</option>
              <option value="john">John Doe</option>
            </select>
          </div>
        </div>
      </div>

      <div className="at__timeline">
        {filteredEntries.map((entry) => (
          <div key={entry.id} className="at__entry card">
            <div className="at__entry-header">
              <span className="at__entry-time">{formatDate(entry.timestamp)}</span>
              <Link
                to={`/submissions/${entry.submissionId}`}
                className="at__entry-submission"
              >
                {entry.submissionId}
              </Link>
              <span
                className={`at__entry-action at__entry-action--${entry.actionType}`}
              >
                {ACTION_LABELS[entry.actionType] || entry.actionType}
              </span>
            </div>

            <div className="at__entry-meta">
              <span className="at__entry-industry">
                {entry.industryCode} · {entry.industryName}
              </span>
            </div>

            <div className="at__entry-score-flow">
              <span className="at__score-step">Base: {entry.baseScore}</span>
              <span className="at__score-arrow">→</span>
              <span className="at__score-tags">
                Tags: {entry.tagsApplied.map((t) => t.adjustment).join(', ')}
              </span>
              <span className="at__score-arrow">→</span>
              <span className="at__score-final">Final: {entry.finalScore}</span>
            </div>

            <div className="at__entry-tags">
              {entry.tagsApplied.map((tag, idx) => (
                <span
                  key={idx}
                  className={`at__tag-pill at__tag-pill--${tag.adjustment >= 0 ? 'plus' : 'minus'}`}
                >
                  {tag.label} {tag.adjustment >= 0 ? '+' : ''}{tag.adjustment}
                </span>
              ))}
            </div>

            <div className="at__entry-confidence">
              <span className="at__confidence-label">Confidence</span>
              <div className="at__confidence-bar-wrap">
                <div
                  className="at__confidence-bar"
                  style={{ width: `${entry.confidence * 100}%` }}
                />
              </div>
              <span className="at__confidence-value">
                {Math.round(entry.confidence * 100)}%
              </span>
            </div>

            <div className="at__entry-footer">
              <span
                className={`badge badge--${entry.hazardTier} at__hazard-badge`}
              >
                {HAZARD_LABELS[entry.hazardTier] || entry.hazardTier} Hazard
              </span>
              {entry.override && (
                <div className="at__override-block">
                  <span className="at__override-label">
                    Override: {entry.override.from} → {entry.override.to}
                  </span>
                  <p className="at__override-reason">{entry.override.reason}</p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
